import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore, GameState } from '../store/gameStore';

interface GardenAreaProps {
  area: keyof GameState['gardenAreas'];
  title: string;
  icon: string;
  description: string;
  specialFeature?: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'expert' | 'legendary';
  unlockLevel?: number;
}

const GardenArea: React.FC<GardenAreaProps> = ({ 
  area, 
  title, 
  icon, 
  description, 
  specialFeature, 
  difficulty, 
  unlockLevel 
}) => {
  const { gardenAreas, coins, playerLevel, renovateArea, renovateAreaBulk, getUpgradeCost, getAreaBonus, upgrades, formatNumber, companions } = useGameStore();
  const [showDetails, setShowDetails] = useState(false);
  const [purchaseMode, setPurchaseMode] = useState<1 | 10 | 100 | 'max'>('max');
  const [selectedCompanion, setSelectedCompanion] = useState<string | null>(null);
  
  const areaData = gardenAreas?.[area];
  
  // Sicherheitsüberprüfung falls areaData undefined ist
  if (!areaData) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <p>Fehler: Gartenbereich "{area}" nicht gefunden.</p>
        <p className="text-xs">Bitte laden Sie die Seite neu.</p>
      </div>
    );
  }

  // Prüfe ob Bereich freigeschaltet ist
  const isUnlocked = !unlockLevel || (playerLevel || 1) >= unlockLevel;
  
  // Berechne Kosten basierend auf Kauf-Modus
  const calculateCosts = () => {
    if (!getUpgradeCost) return { cost: 100, quantity: 1, actualQuantity: 1 };
    
    const maxPossibleLevels = areaData.maxLevel - areaData.level;
    let quantity = 1;
    
    if (purchaseMode === 10) quantity = Math.min(10, maxPossibleLevels);
    else if (purchaseMode === 100) quantity = Math.min(100, maxPossibleLevels);
    else if (purchaseMode === 'max') quantity = maxPossibleLevels;
    
    if (quantity <= 0) return { cost: 0, quantity: 0, actualQuantity: 0 };
    
    // Berechne Gesamtkosten
    let totalCost = 0;
    let affordableQuantity = 0;
    
    for (let i = 0; i < quantity; i++) {
      const levelCost = getUpgradeCost('renovation', areaData.level + i);
      if (totalCost + levelCost <= (coins || 0)) {
        totalCost += levelCost;
        affordableQuantity++;
      } else {
        break;
      }
    }
    
    return { cost: totalCost, quantity, actualQuantity: affordableQuantity };
  };

  const { cost: upgradeCost, quantity: requestedQuantity, actualQuantity } = calculateCosts();
  const canUpgrade = areaData.level < areaData.maxLevel;
  const canAfford = (coins || 0) >= upgradeCost && actualQuantity > 0;
  const areaBonus = getAreaBonus ? getAreaBonus(area) : areaData.level * 2;
  
  // Prüfe alle Begleiter die gerade diesen Bereich renovieren
  const companionsOnRenovation = companions?.discovered.filter(c => 
    c.isOnJourney && c.journeyType === 'renovation' && c.journeyArea === area
  ) || [];
  
  // Berechne verbleibende Zeit für einen spezifischen Begleiter
  const getRenovationTimeRemaining = (companion: any) => {
    if (!companion?.journeyStartTime || !companion?.journeyDuration) return null;
    
    const startTime = new Date(companion.journeyStartTime);
    const endTime = new Date(startTime.getTime() + companion.journeyDuration * 60 * 60 * 1000);
    const now = new Date();
    const remainingMs = endTime.getTime() - now.getTime();
    
    if (remainingMs > 0) {
      const hours = Math.floor(remainingMs / (1000 * 60 * 60));
      const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));
      return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    }
    return null;
  };
  
  // Berechne Zeit für ein Level basierend auf Begleiter-Effizienz
  const getTimePerLevel = (companion: any) => {
    if (!companion?.journeyDuration) return null;
    
    // Basis-Zeit für ein Level (in Stunden) - effizienter als vorher angezeigt
    // Begleiter mit höherer Seltenheit sind effizienter
    const baseTimePerLevel = companion.type === 'legendary' ? 0.5 : 
                            companion.type === 'epic' ? 0.67 : 
                            companion.type === 'rare' ? 0.83 : 1;
    
    const hours = Math.floor(baseTimePerLevel);
    const minutes = Math.floor((baseTimePerLevel % 1) * 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };
  
  // Berechne wie viele Level der Begleiter bereits geschafft hat
  const getCompanionProgress = (companion: any) => {
    if (!companion?.journeyStartTime || !companion?.journeyStartLevel) return null;
    
    const startTime = new Date(companion.journeyStartTime);
    const now = new Date();
    const elapsedHours = (now.getTime() - startTime.getTime()) / (1000 * 60 * 60);
    
    const efficiency = companion.type === 'legendary' ? 2 : 
                      companion.type === 'epic' ? 1.5 : 
                      companion.type === 'rare' ? 1.2 : 1;
    
    const expectedLevels = Math.floor(elapsedHours * efficiency);
    const actualProgress = areaData.level - companion.journeyStartLevel;
    
    return { expectedLevels, actualProgress };
  };

  const handleRenovate = () => {
    if (!canUpgrade || !canAfford) return;
    
    if (purchaseMode === 1) {
      if (renovateArea) {
        renovateArea(area);
      }
    } else {
      if (renovateAreaBulk) {
        renovateAreaBulk(area, requestedQuantity);
      }
    }
  };

  // Berechne Fortschrittsbalken
  const progressPercentage = (areaData.level / areaData.maxLevel) * 100;

  // Bestimme Schwierigkeitsfarbe
  const getDifficultyColor = () => {
    switch (difficulty) {
      case 'easy': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'hard': return 'text-orange-600';
      case 'expert': return 'text-red-600';
      case 'legendary': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  // Bestimme Hintergrundfarbe basierend auf Level und Schwierigkeit
  const getBackgroundClass = () => {
    if (!isUnlocked) return 'from-gray-100 to-gray-200 opacity-60';
    if (areaData.level === 0) return 'from-gray-200 to-gray-300';
    if (areaData.level === areaData.maxLevel) {
      switch (difficulty) {
        case 'easy': return 'from-green-200 to-green-400';
        case 'medium': return 'from-yellow-200 to-yellow-400';
        case 'hard': return 'from-orange-200 to-orange-400';
        case 'expert': return 'from-red-200 to-red-400';
        case 'legendary': return 'from-purple-200 via-pink-200 to-purple-400';
        default: return 'from-garden-200 to-garden-400';
      }
    }
    return 'from-garden-100 to-garden-200';
  };

  const getDescriptionByLevel = () => {
    if (!isUnlocked) return `Freischaltung bei Level ${unlockLevel}`;
    if (areaData.level === 0) return 'Brachland - Bereit zur Renovierung';
    if (areaData.level === areaData.maxLevel) return 'Vollständig renoviert! 🌟';
    return `${description} - Level ${areaData.level}`;
  };

  // Spezielle Effekte basierend auf Upgrades
  const hasSpecialEffects = () => {
    return (upgrades?.plantWhisperer || 0) > 0 || 
           (upgrades?.weatherStation || 0) > 0 || 
           (upgrades?.gardenLighting || 0) > 0;
  };

  if (!isUnlocked) {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="relative overflow-hidden rounded-lg border-2 border-purple-300/50 shadow-lg cursor-pointer group"
      >
        {/* Mysteriöser Hintergrund mit Animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/80 via-indigo-800/70 to-slate-900/90">
          {/* Animierte Partikel */}
          <motion.div
            animate={{ 
              opacity: [0.3, 0.7, 0.3],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{ duration: 3, repeat: Infinity }}
            className="absolute top-1/4 left-1/4 text-purple-400/60 text-lg"
          >
            ✨
          </motion.div>
          <motion.div
            animate={{ 
              opacity: [0.2, 0.6, 0.2],
              scale: [0.6, 1.1, 0.6]
            }}
            transition={{ duration: 4, repeat: Infinity, delay: 1 }}
            className="absolute top-3/4 right-1/4 text-blue-400/50 text-sm"
          >
            💫
          </motion.div>
          <motion.div
            animate={{ 
              opacity: [0.4, 0.8, 0.4],
              scale: [0.7, 1.3, 0.7]
            }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 2 }}
            className="absolute bottom-1/4 left-1/3 text-pink-400/40 text-base"
          >
            🌟
          </motion.div>
        </div>

        {/* Nebel-Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-purple-500/10 opacity-60" />
        
        {/* Glasmorphismus-Effekt */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-purple-500/5 backdrop-blur-sm" />

        {/* Hauptinhalt */}
        <div className="relative z-10 p-4 text-center">
          {/* Mysteriöses Icon mit Glow-Effekt */}
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ duration: 4, repeat: Infinity }}
            className="text-4xl mb-3 filter drop-shadow-[0_0_10px_rgba(168,85,247,0.6)]"
          >
            {icon}
          </motion.div>

          {/* Titel mit mysteriösem Glow */}
          <h3 className="font-bold text-lg mb-2 text-white drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]">
            {title}
          </h3>

          {/* Schwierigkeitsindikator */}
          <div className={`inline-block px-2 py-1 rounded-full text-xs font-bold mb-3 ${getDifficultyColor()} bg-black/30 backdrop-blur-sm`}>
            {difficulty.toUpperCase()}
          </div>

          {/* Level-Anforderung mit Animation */}
          <motion.div
            animate={{ 
              opacity: [0.7, 1, 0.7],
              scale: [0.95, 1.05, 0.95]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="bg-gradient-to-r from-purple-600/80 to-pink-600/80 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-lg"
          >
            🔒 Level {unlockLevel} benötigt
          </motion.div>

          {/* Mysteriöser Hinweis */}
          <motion.div
            animate={{ opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="text-purple-200 text-xs mt-2 italic"
          >
            "Ein verborgenes Geheimnis wartet..."
          </motion.div>
        </div>

        {/* Pulsierender Rand */}
        <motion.div
          animate={{ 
            boxShadow: [
              "0 0 0 0 rgba(168, 85, 247, 0.4)",
              "0 0 0 10px rgba(168, 85, 247, 0)",
              "0 0 0 0 rgba(168, 85, 247, 0)"
            ]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute inset-0 rounded-lg pointer-events-none"
        />

        {/* Hover-Effekt */}
        <motion.div
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
          className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg pointer-events-none"
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`garden-area bg-gradient-to-br ${getBackgroundClass()} relative overflow-hidden rounded-lg border border-gray-200 shadow-md`}
    >
      {/* Begleiter-Renovierung Anzeige */}
      {companionsOnRenovation.length > 0 && (
        <div className="absolute top-1 left-1 space-y-1">
          {companionsOnRenovation.map((companion, index) => (
            <div key={companion.id} className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCompanion(selectedCompanion === companion.id ? null : companion.id);
                }}
                className={`bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg border-2 border-white relative transition-colors cursor-pointer ${
                  selectedCompanion === companion.id ? 'ring-2 ring-yellow-400' : ''
                }`}
              >
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.2 }}
                  className="flex items-center gap-1"
                >
                  <span>{companion.icon}</span>
                  <span>🔨</span>
                  <span>{getRenovationTimeRemaining(companion)}</span>
                </motion.div>
              </button>
              
              {/* Erweiterte Info-Box */}
              {selectedCompanion === companion.id && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute top-full left-0 mt-2 bg-black text-white text-xs px-3 py-2 rounded-lg shadow-xl z-50 min-w-[200px]"
                >
                  <div className="font-bold text-yellow-300 mb-1">{companion.name}</div>
                  <div className="text-gray-300 mb-2">renoviert diesen Bereich</div>
                  
                  <div className="space-y-1">
                    <div className="text-yellow-300">⚡ {getTimePerLevel(companion)} pro Level</div>
                    
                    {(() => {
                      const progress = getCompanionProgress(companion);
                      return progress ? (
                        <div className="text-green-300">
                          📈 {progress.actualProgress} Level geschafft
                          {progress.expectedLevels > progress.actualProgress && 
                            ` (${progress.expectedLevels - progress.actualProgress} ausstehend)`
                          }
                        </div>
                      ) : null;
                    })()}
                    
                    <div className="text-blue-300">
                      ⏰ Verbleibend: {getRenovationTimeRemaining(companion)}
                    </div>
                    
                    <div className="text-gray-300 pt-1 border-t border-gray-600">
                      {companion.type === 'legendary' ? '⭐ Legendär (2x Effizienz)' :
                       companion.type === 'epic' ? '🔥 Episch (1.5x Effizienz)' :
                       companion.type === 'rare' ? '💎 Selten (1.2x Effizienz)' :
                       '🌟 Gewöhnlich (1x Effizienz)'
                      }
                    </div>
                  </div>
                  
                  <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-black"></div>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Spezielle Effekte */}
      {hasSpecialEffects() && areaData.level > 0 && companionsOnRenovation.length === 0 && (
        <div className="absolute top-1 left-1 flex gap-1">
          {(upgrades?.plantWhisperer || 0) > 0 && (
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-green-400 text-xs"
            >
              🌿
            </motion.div>
          )}
          {(upgrades?.weatherStation || 0) > 0 && Math.random() < 0.3 && (
            <motion.div
              animate={{ y: [-5, 5, -5] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="text-blue-400 text-xs"
            >
              🌤️
            </motion.div>
          )}
          {(upgrades?.gardenLighting || 0) > 0 && (
            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-yellow-400 text-xs"
            >
              💡
            </motion.div>
          )}
        </div>
      )}

      {/* Arbeits-Animation bei Begleiter-Renovierung */}
      {companionsOnRenovation.length > 0 && (
        <>
          {/* Animierte Staub-Partikel */}
          <motion.div
            animate={{ 
              opacity: [0.3, 0.8, 0.3],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute top-2 right-2 text-yellow-600"
          >
            💨
          </motion.div>
          

          
          {/* Arbeits-Werkzeuge Animation */}
          <motion.div
            animate={{ 
              x: [-2, 2, -2],
              y: [-1, 1, -1]
            }}
            transition={{ duration: 0.8, repeat: Infinity }}
            className="absolute bottom-2 right-2 text-orange-600"
          >
            ⚒️
          </motion.div>
        </>
      )}

      {/* Info Button */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="absolute top-1 right-1 bg-gray-600 hover:bg-gray-700 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs transition-colors z-20"
        title="Details anzeigen"
      >
        ℹ️
      </button>

      {/* Sparkle-Animation bei vollständiger Renovierung */}
      {areaData.level === areaData.maxLevel && companionsOnRenovation.length === 0 && (
        <div className="absolute top-7 right-1">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="text-yellow-400"
          >
            ✨
          </motion.div>
        </div>
      )}

      {/* Schwierigkeitsindikator */}
      <div className={`absolute top-2 left-2 text-xs font-bold ${getDifficultyColor()}`}>
        {difficulty.toUpperCase()}
      </div>

      <div className="text-center p-1.5 sm:p-3">
        <motion.div 
          className="text-2xl sm:text-3xl mb-1"
          animate={areaData.level === areaData.maxLevel ? { 
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0] 
          } : {}}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {icon}
        </motion.div>
        <h3 className="font-semibold text-xs sm:text-sm mb-1 leading-tight">{title}</h3>
        
        {/* Fortschrittsbalken */}
        <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 mb-1.5 relative">
          <motion.div
            className={`h-2 sm:h-3 rounded-full ${
              areaData.level === areaData.maxLevel 
                ? 'bg-gradient-to-r from-yellow-400 to-orange-400' 
                : 'bg-garden-500'
            }`}
            style={{ width: `${progressPercentage}%` }}
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5 }}
          />
          <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-black">
            {Math.round(progressPercentage)}%
          </div>
        </div>

        <p className="text-xs text-gray-600 mb-1.5 min-h-[1rem] sm:min-h-[1.5rem] leading-tight">
          {getDescriptionByLevel()}
        </p>

        {/* Bonus-Anzeige */}
        {areaData.level > 0 && (
          <div className="bg-blue-50 rounded p-1 mb-1.5">
            <div className="text-xs text-blue-600 font-medium">
              +{formatNumber(areaBonus)}/Min
            </div>
            {specialFeature && (
              <div className="text-xs text-purple-600 truncate" title={specialFeature}>
                {specialFeature}
              </div>
            )}
            {/* Meilenstein-Anzeige */}
            {areaData.level >= 25 && areaData.level < 50 && (
              <div className="text-xs text-orange-600 font-bold">
                🏅 Fortgeschritten!
              </div>
            )}
            {areaData.level >= 50 && areaData.level < 75 && (
              <div className="text-xs text-red-600 font-bold">
                🔥 Experte!
              </div>
            )}
            {areaData.level >= 75 && areaData.level < 100 && (
              <div className="text-xs text-purple-600 font-bold">
                ✨ Meister!
              </div>
            )}
            {areaData.level === 100 && (
              <div className="text-xs text-yellow-600 font-bold animate-pulse">
                👑 PERFEKTION!
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center text-xs mb-1.5">
          <span className="font-medium">Level {areaData.level}/{areaData.maxLevel}</span>
          {canUpgrade && (
            <div className="text-right">
              <div className="text-yellow-600 font-bold">
                💰 {formatNumber(upgradeCost > 0 ? upgradeCost : (getUpgradeCost ? getUpgradeCost('renovation', areaData.level) : 100))}
              </div>
            </div>
          )}
        </div>

        {canUpgrade ? (
          <div 
            className="space-y-1.5"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Begleiter-Info wenn welche arbeiten */}
            {companionsOnRenovation.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded p-1.5 mb-1.5">
                <div className="text-xs text-blue-700 font-medium mb-0.5">
                  🔨 {companionsOnRenovation.length} Begleiter
                </div>
                <div className="text-xs text-blue-600">
                  Zusätzlich renovieren möglich!
                </div>
              </div>
            )}
            
            {/* Kauf-Modus Buttons */}
            <div className="grid grid-cols-4 gap-1 mb-1.5">
              {[1, 10, 100, 'max'].map((mode) => (
                <button
                  key={mode}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPurchaseMode(mode as typeof purchaseMode);
                  }}
                  className={`px-1.5 py-1 text-xs rounded transition-colors min-h-[32px] ${
                    purchaseMode === mode
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {mode === 'max' ? 'MAX' : `${mode}x`}
                </button>
              ))}
            </div>
            
            {/* Renovieren Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                handleRenovate();
              }}
              disabled={!canAfford}
              className={`btn-primary w-full text-xs sm:text-sm py-3 min-h-[44px] ${
                !canAfford ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                companionsOnRenovation.length > 0 ? 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600' : ''
              }`}
            >
              {canAfford ? (
                companionsOnRenovation.length > 0 ? 
                  (purchaseMode === 1 ? '⚡ Zusätzlich' : `⚡ ${actualQuantity}x`) :
                  (purchaseMode === 1 ? '🔨 Renovieren' : `🔨 ${actualQuantity}x`)
              ) : (
                // Zeige die Kosten basierend auf dem Kauf-Modus
                (() => {
                  // Berechne die Kosten für den nächsten einzelnen Level wenn upgradeCost 0 ist
                  const nextLevelCost = upgradeCost > 0 ? upgradeCost : (getUpgradeCost ? getUpgradeCost('renovation', areaData.level) : 100);
                  const displayMode = purchaseMode === 'max' ? '1x' : `${purchaseMode}x`;
                  
                  return companionsOnRenovation.length > 0 ? 
                    (purchaseMode === 'max' ? `⚡ 💰 ${formatNumber(nextLevelCost)}` : `⚡ ${displayMode} 💰 ${formatNumber(nextLevelCost)}`) :
                    (purchaseMode === 'max' ? `🔨 💰 ${formatNumber(nextLevelCost)}` : `🔨 ${displayMode} 💰 ${formatNumber(nextLevelCost)}`);
                })()
              )}
            </motion.button>

          </div>
        ) : (
          <div className="text-center py-2 text-green-600 font-semibold bg-green-50 rounded-lg">
            ✅ Vollständig renoviert!
          </div>
        )}
      </div>

      {/* Details-Panel */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="absolute inset-0 bg-white bg-opacity-95 backdrop-blur-sm rounded-lg p-3 sm:p-4 z-10 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDetails(false);
              }}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
            
            <div className="text-center">
              <div className="text-2xl sm:text-3xl mb-2">{icon}</div>
              <h3 className="font-bold text-sm sm:text-lg mb-2">{title}</h3>
              <div className={`text-xs sm:text-sm font-medium mb-2 ${getDifficultyColor()}`}>
                {difficulty.toUpperCase()}
              </div>
              
              <div className="text-left space-y-2 text-xs sm:text-sm">
                <div><strong>Info:</strong> {description}</div>
                {specialFeature && (
                  <div><strong>Bonus:</strong> {specialFeature}</div>
                )}
                <div><strong>Max:</strong> {areaData.maxLevel}</div>
                <div><strong>Einkommen:</strong> +{areaBonus}/Min</div>
                {areaData.level === areaData.maxLevel && (
                  <div className="text-green-600 font-bold text-center">
                    🌟 Meisterwerk! 🌟
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const GardenSection: React.FC = () => {
  const { gardenAreas, playerLevel, repairGardenAreas, achievements, upgrades, formatNumber, coins } = useGameStore();
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'easy' | 'medium' | 'hard' | 'expert' | 'legendary'>('all');
  const [showStats, setShowStats] = useState(false);
  const [currentWeather, setCurrentWeather] = useState<'sunny' | 'rainy' | 'cloudy' | 'stormy'>('sunny');
  
  // Repariere Gartenbereiche falls nötig
  React.useEffect(() => {
    if (repairGardenAreas) {
      repairGardenAreas();
    }
  }, [repairGardenAreas]);

  // Berechne Gesamtfortschritt
  const totalProgress = Object.values(gardenAreas || {}).reduce((acc, area) => {
    return acc + (area.level / area.maxLevel);
  }, 0);
  const totalAreas = Object.keys(gardenAreas || {}).length;
  const overallProgress = (totalProgress / totalAreas) * 100;

  const gardenAreaConfigs = [
    // Basis-Bereiche (Easy)
    {
      area: 'lawn' as const,
      title: 'Rasenfläche',
      icon: '🌱',
      description: 'Ein wunderschöner grüner Rasen als Basis des Gartens',
      specialFeature: 'Basis-Einkommen für alle anderen Bereiche',
      difficulty: 'easy' as const,
    },
    {
      area: 'flowerBed' as const,
      title: 'Blumenbeet',
      icon: '🌸',
      description: 'Bunte Blumen locken Bienen und Schmetterlinge an',
      specialFeature: 'Bonus-Edelsteine durch Bestäubung',
      difficulty: 'easy' as const,
    },
    
    // Standard-Bereiche (Medium)
    {
      area: 'pond' as const,
      title: 'Gartenteich',
      icon: '🐸',
      description: 'Ein friedlicher Teich mit Fischen und Seerosen',
      specialFeature: 'Entspannung steigert XP-Gewinn',
      difficulty: 'medium' as const,
      unlockLevel: 3,
    },
    {
      area: 'terrace' as const,
      title: 'Terrasse',
      icon: '🪑',
      description: 'Gemütliche Sitzgelegenheit für Gartenpartys',
      specialFeature: 'Erhöht Glücksfaktor aller Bereiche',
      difficulty: 'medium' as const,
      unlockLevel: 2,
    },
    {
      area: 'roseGarden' as const,
      title: 'Rosengarten',
      icon: '🌹',
      description: 'Prächtige Rosen in allen Farben und Düften',
      specialFeature: 'Romantik-Bonus verdoppelt Edelstein-Chance',
      difficulty: 'medium' as const,
      unlockLevel: 4,
    },
    {
      area: 'wildflowerMeadow' as const,
      title: 'Wildblumenwiese',
      icon: '🌼',
      description: 'Natürliche Wildblumen für die lokale Tierwelt',
      specialFeature: 'Biodiversitäts-Bonus für alle Bereiche',
      difficulty: 'medium' as const,
      unlockLevel: 6,
    },
    
    // Schwere Bereiche (Hard)
    {
      area: 'greenhouse' as const,
      title: 'Gewächshaus',
      icon: '🌿',
      description: 'Schutz für empfindliche und exotische Pflanzen',
      specialFeature: 'Wetterunabhängiges Wachstum',
      difficulty: 'hard' as const,
      unlockLevel: 5,
    },
    {
      area: 'fountain' as const,
      title: 'Springbrunnen',
      icon: '⛲',
      description: 'Eleganter Wasserspender als Garten-Herzstück',
      specialFeature: 'Verstärkt alle anderen Bereiche',
      difficulty: 'hard' as const,
      unlockLevel: 7,
    },
    {
      area: 'japaneseGarden' as const,
      title: 'Zen-Garten',
      icon: '🎋',
      description: 'Japanischer Garten für Meditation und innere Ruhe',
      specialFeature: 'Meditation verstärkt XP-Gewinn um 100%',
      difficulty: 'hard' as const,
      unlockLevel: 8,
    },
    {
      area: 'sculptureGarden' as const,
      title: 'Skulpturengarten',
      icon: '🗿',
      description: 'Kunstvolle Skulpturen zwischen grünen Pflanzen',
      specialFeature: 'Kunst-Inspiration erhöht Klick-Multiplier',
      difficulty: 'hard' as const,
      unlockLevel: 9,
    },
    
    // Experten-Bereiche (Expert)
    {
      area: 'orchard' as const,
      title: 'Obstgarten',
      icon: '🍎',
      description: 'Frische Früchte aus eigenem Anbau das ganze Jahr',
      specialFeature: 'Saisonale Bonus-Ernten',
      difficulty: 'expert' as const,
      unlockLevel: 10,
    },
    {
      area: 'veggiePatch' as const,
      title: 'Gemüsebeet',
      icon: '🥕',
      description: 'Gesundes Bio-Gemüse für die nachhaltige Küche',
      specialFeature: 'Gesundheits-Bonus erhöht Effizienz',
      difficulty: 'expert' as const,
      unlockLevel: 12,
    },
    
    // Klick-fokussierte Bereiche (Expert)
    {
      area: 'goldMine' as const,
      title: 'Goldmine',
      icon: '⛏️',
      description: 'Tiefe Mine voller glänzender Goldadern',
      specialFeature: 'Jeder Level +5% Münzen pro Klick',
      difficulty: 'expert' as const,
      unlockLevel: 15,
    },
    {
      area: 'treasureVault' as const,
      title: 'Schatzkammer',
      icon: '💰',
      description: 'Sichere Kammer für die wertvollsten Schätze',
      specialFeature: 'Jeder Level +8% Münzen pro Klick',
      difficulty: 'expert' as const,
      unlockLevel: 18,
    },
    
    // Premium-Bereiche (Legendary)
    {
      area: 'enchantedGrove' as const,
      title: 'Verzauberter Hain',
      icon: '🧚‍♀️',
      description: 'Magischer Wald voller Geheimnisse und Wunder',
      specialFeature: 'Feenmagie verdreifacht alle Boni',
      difficulty: 'legendary' as const,
      unlockLevel: 15,
    },
    {
      area: 'crystalCave' as const,
      title: 'Kristallhöhle',
      icon: '💎',
      description: 'Unterirdische Höhle voller leuchtender Kristalle',
      specialFeature: 'Kristall-Resonanz generiert Edelsteine passiv',
      difficulty: 'legendary' as const,
      unlockLevel: 18,
    },
    {
      area: 'moonlightGarden' as const,
      title: 'Mondschein-Garten',
      icon: '🌙',
      description: 'Nächtlicher Garten, der nur im Mondlicht blüht',
      specialFeature: 'Mondphasen-Bonus variiert mit der Zeit',
      difficulty: 'legendary' as const,
      unlockLevel: 20,
    },
    {
      area: 'dragonLair' as const,
      title: 'Drachenhort',
      icon: '🐉',
      description: 'Legendärer Drachenhort mit unermesslichen Schätzen',
      specialFeature: 'Drachensegen multipliziert alle Einkommen x10',
      difficulty: 'legendary' as const,
      unlockLevel: 25,
    },
    {
      area: 'alchemyLab' as const,
      title: 'Alchemie-Labor',
      icon: '⚗️',
      description: 'Mystisches Labor zur Goldherstellung',
      specialFeature: 'Jeder Level +12% Münzen pro Klick',
      difficulty: 'legendary' as const,
      unlockLevel: 22,
    },
    {
      area: 'magicForge' as const,
      title: 'Magische Schmiede',
      icon: '🔥',
      description: 'Uralte Schmiede mit magischen Verstärkungen',
      specialFeature: 'Jeder Level +15% Münzen pro Klick',
      difficulty: 'legendary' as const,
      unlockLevel: 30,
    },
  ];

  // Filtere Bereiche nach Kategorie
  const filteredAreas = gardenAreaConfigs.filter(area => 
    selectedCategory === 'all' || area.difficulty === selectedCategory
  );

  // Statistiken berechnen
  const completedAreas = Object.values(gardenAreas || {}).filter(area => 
    area.level === area.maxLevel
  ).length;

  const totalIncome = Object.entries(gardenAreas || {}).reduce((sum, [key, area]) => {
    if (area.level > 0) {
      return sum + (area.level * 2);
    }
    return sum;
  }, 0);

  const unlockedAreas = gardenAreaConfigs.filter(config => 
    !config.unlockLevel || (playerLevel || 1) >= config.unlockLevel
  ).length;

  // Wettersystem - ändert sich alle 2 Minuten
  React.useEffect(() => {
    const weatherTypes: ('sunny' | 'rainy' | 'cloudy' | 'stormy')[] = ['sunny', 'rainy', 'cloudy', 'stormy'];
    const interval = setInterval(() => {
      const randomWeather = weatherTypes[Math.floor(Math.random() * weatherTypes.length)];
      setCurrentWeather(randomWeather);
    }, 120000); // 2 Minuten

    return () => clearInterval(interval);
  }, []);

  const getWeatherInfo = () => {
    switch (currentWeather) {
      case 'sunny':
        return { icon: '☀️', name: 'Sonnig', effect: '+20% Wachstum', color: 'text-yellow-600' };
      case 'rainy':
        return { icon: '🌧️', name: 'Regnerisch', effect: '+30% Wasserpflanzen', color: 'text-blue-600' };
      case 'cloudy':
        return { icon: '☁️', name: 'Bewölkt', effect: 'Normale Bedingungen', color: 'text-gray-600' };
      case 'stormy':
        return { icon: '⛈️', name: 'Stürmisch', effect: '-10% Wachstum', color: 'text-purple-600' };
      default:
        return { icon: '☀️', name: 'Sonnig', effect: '+20% Wachstum', color: 'text-yellow-600' };
    }
  };

  const weather = getWeatherInfo();

  return (
    <div className="garden-section space-y-4 sm:space-y-6">
      {/* Header mit erweiterten Statistiken - Mobile optimiert */}
      <div className="bg-gradient-to-r from-green-50 via-blue-50 to-purple-50 rounded-xl p-3 sm:p-6 shadow-lg">
        <div className="text-center mb-3 sm:mb-6">
          <h2 className="text-lg sm:text-3xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-garden-700 to-green-600 bg-clip-text text-transparent">
            🏡 Mein Traumgarten
          </h2>
          <p className="text-xs sm:text-base text-gray-600">Verwandle dein Brachland in ein Paradies!</p>
          
          {/* Wetter-Widget */}
          {(upgrades?.weatherStation || 0) > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-md"
            >
              <motion.div
                animate={currentWeather === 'stormy' ? { rotate: [0, 5, -5, 0] } : {}}
                transition={{ duration: 0.5, repeat: currentWeather === 'stormy' ? Infinity : 0 }}
                className="text-xl"
              >
                {weather.icon}
              </motion.div>
              <div className="text-sm">
                <div className={`font-semibold ${weather.color}`}>{weather.name}</div>
                <div className="text-gray-500">{weather.effect}</div>
              </div>
            </motion.div>
          )}

          <div className="mt-3 flex justify-center gap-1 sm:gap-2">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowStats(!showStats)}
              className="px-3 sm:px-4 py-2 bg-garden-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-garden-600 transition-colors"
            >
              {showStats ? '📊 Weniger' : '📊 Mehr'}
            </motion.button>
            
            {(upgrades?.weatherStation || 0) > 0 && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const weatherTypes: ('sunny' | 'rainy' | 'cloudy' | 'stormy')[] = ['sunny', 'rainy', 'cloudy', 'stormy'];
                  const randomWeather = weatherTypes[Math.floor(Math.random() * weatherTypes.length)];
                  setCurrentWeather(randomWeather);
                }}
                className="px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-600 transition-colors"
              >
                🌤️ Wetter
              </motion.button>
            )}
          </div>
        </div>

        {/* Gesamtfortschritt */}
        <div className="mb-2 sm:mb-4">
          <div className="flex justify-between items-center mb-1 sm:mb-2">
            <span className="font-medium text-xs sm:text-base text-gray-700">Gesamtfortschritt</span>
            <span className="text-xs sm:text-sm text-gray-600">
              {Math.round(totalProgress)}/{totalAreas} Bereiche
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 sm:h-5 relative overflow-hidden">
            <motion.div
              className="bg-gradient-to-r from-garden-400 via-green-500 to-garden-600 h-4 sm:h-5 rounded-full"
              style={{ width: `${overallProgress}%` }}
              initial={{ width: 0 }}
              animate={{ width: `${overallProgress}%` }}
              transition={{ duration: 1 }}
            />
            <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-black">
              {Math.round(overallProgress)}%
            </div>
          </div>
        </div>

        {/* Basis-Statistiken - Mobile optimiert */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-3">
          <div className="bg-white rounded-lg p-2 sm:p-4 shadow-sm text-center">
            <div className="text-base sm:text-2xl font-bold text-green-600">{completedAreas}</div>
            <div className="text-xs text-gray-600">Fertige</div>
          </div>
          <div className="bg-white rounded-lg p-2 sm:p-4 shadow-sm text-center">
            <div className="text-base sm:text-2xl font-bold text-blue-600">+{formatNumber(totalIncome)}</div>
            <div className="text-xs text-gray-600">Münzen/Min</div>
          </div>
          <div className="bg-white rounded-lg p-2 sm:p-4 shadow-sm text-center">
            <div className="text-base sm:text-2xl font-bold text-purple-600">{(playerLevel || 1)}</div>
            <div className="text-xs text-gray-600">Level</div>
          </div>
          <div className="bg-white rounded-lg p-2 sm:p-4 shadow-sm text-center">
            <div className="text-base sm:text-2xl font-bold text-orange-600">{unlockedAreas}</div>
            <div className="text-xs text-gray-600">Frei</div>
          </div>
        </div>

        {/* Erweiterte Statistiken */}
        <AnimatePresence>
          {showStats && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 sm:mt-4 grid grid-cols-3 gap-1.5 sm:gap-3"
            >
              <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm text-center">
                <div className="text-sm sm:text-lg font-bold text-red-600">
                  {gardenAreaConfigs.filter(a => a.difficulty === 'expert').length}
                </div>
                <div className="text-xs text-gray-600">Experten</div>
              </div>
              <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm text-center">
                <div className="text-sm sm:text-lg font-bold text-yellow-600">
                  {formatNumber(achievements?.totalCoinsEarned || 0)}
                </div>
                <div className="text-xs text-gray-600">Verdient</div>
              </div>
              <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm text-center">
                <div className="text-sm sm:text-lg font-bold text-pink-600">
                  {Object.values(gardenAreas || {}).reduce((sum, area) => sum + area.level, 0)}
                </div>
                <div className="text-xs text-gray-600">Total</div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Kategorie-Filter - Mobile optimiert */}
      <div className="bg-white rounded-lg p-2 sm:p-3 shadow-md">
        <h3 className="font-semibold mb-1.5 sm:mb-2 text-center text-xs sm:text-sm">Filter</h3>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1 sm:gap-2 sm:justify-center">
          {[
            { key: 'all', label: 'Alle', color: 'bg-gray-500', count: gardenAreaConfigs.length },
            { key: 'easy', label: 'Einfach', color: 'bg-green-500', count: gardenAreaConfigs.filter(a => a.difficulty === 'easy').length },
            { key: 'medium', label: 'Mittel', color: 'bg-yellow-500', count: gardenAreaConfigs.filter(a => a.difficulty === 'medium').length },
            { key: 'hard', label: 'Schwer', color: 'bg-orange-500', count: gardenAreaConfigs.filter(a => a.difficulty === 'hard').length },
            { key: 'expert', label: 'Experte', color: 'bg-red-500', count: gardenAreaConfigs.filter(a => a.difficulty === 'expert').length },
            { key: 'legendary', label: 'Legendär', color: 'bg-purple-500', count: gardenAreaConfigs.filter(a => a.difficulty === 'legendary').length }
          ].map(category => (
            <motion.button
              key={category.key}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategory(category.key as any)}
              className={`px-2 sm:px-4 py-2 rounded-full text-white text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 min-h-[36px] ${
                selectedCategory === category.key 
                  ? `${category.color} shadow-lg ring-2 ring-white` 
                  : `${category.color} opacity-60 hover:opacity-80`
              }`}
            >
              <span className="truncate">{category.label}</span>
              <span className="bg-white bg-opacity-20 px-1 sm:px-2 py-1 rounded-full text-xs">
                {category.count}
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Gartenbereiche Grid mit Animation - Mobile optimiert */}
      <motion.div 
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3"
        layout
      >
        <AnimatePresence>
          {filteredAreas.map((config) => (
            <motion.div
              key={config.area}
              layout
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <GardenArea
                area={config.area}
                title={config.title}
                icon={config.icon}
                description={config.description}
                specialFeature={config.specialFeature}
                difficulty={config.difficulty}
                unlockLevel={config.unlockLevel}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      {/* Achievements und Belohnungen */}
      {completedAreas >= 4 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-4 sm:p-6 text-center shadow-lg"
        >
          <div className="text-3xl sm:text-4xl mb-3">🏆</div>
          <h3 className="font-bold text-yellow-800 mb-2 text-lg sm:text-xl">Garten-Meister!</h3>
          <p className="text-yellow-700 mb-4 text-sm sm:text-base">
            Du hast {completedAreas} Bereiche fertiggestellt. Dein Garten wird zu einem wahren Paradies!
          </p>
          <div className="grid grid-cols-2 sm:flex sm:justify-center gap-2 sm:gap-4 text-sm">
            <div className="bg-white rounded-lg px-2 sm:px-3 py-2 shadow-sm">
              <div className="font-bold text-green-600 text-sm sm:text-base">+{completedAreas * 50}</div>
              <div className="text-gray-600 text-xs">Bonus-XP</div>
            </div>
            <div className="bg-white rounded-lg px-2 sm:px-3 py-2 shadow-sm">
              <div className="font-bold text-blue-600 text-sm sm:text-base">+{completedAreas * 10}</div>
              <div className="text-gray-600 text-xs">Edelsteine</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Vollständiger Garten */}
      {overallProgress === 100 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gradient-to-r from-yellow-100 via-orange-100 to-red-100 border-4 border-yellow-400 rounded-xl p-4 sm:p-8 text-center shadow-2xl"
        >
          <div className="text-5xl sm:text-8xl mb-4 animate-bounce">👑</div>
          <h2 className="text-2xl sm:text-4xl font-bold text-yellow-800 mb-4 animate-pulse">
            ULTIMATIVE PERFEKTION!
          </h2>
          <p className="text-sm sm:text-xl text-yellow-700 mb-6">
            UNGLAUBLICH! Du hast alle 20 Gartenbereiche auf Level 1000 gebracht! 
            Du bist der absolute Garten-Gott und Meister aller Bereiche!
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-6">
            <div className="bg-white rounded-lg p-2 sm:p-4 shadow-lg border-2 border-yellow-300">
              <div className="text-xl sm:text-3xl font-bold text-yellow-600">👑</div>
              <div className="text-xs sm:text-sm text-gray-600">Garten-Gott</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-4 shadow-lg border-2 border-blue-300">
              <div className="text-xl sm:text-3xl font-bold text-blue-600">+10K</div>
              <div className="text-xs sm:text-sm text-gray-600">Belohnung</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-4 shadow-lg border-2 border-purple-300">
              <div className="text-xl sm:text-3xl font-bold text-purple-600">+1K</div>
              <div className="text-xs sm:text-sm text-gray-600">Edelsteine</div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-4 shadow-lg border-2 border-orange-300">
              <div className="text-xl sm:text-3xl font-bold text-orange-600">x10</div>
              <div className="text-xs sm:text-sm text-gray-600">Mega-Bonus</div>
            </div>
          </div>
          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-3 sm:px-6 py-3 rounded-full text-sm sm:text-lg font-bold shadow-lg">
            🎯 20.000 Level erreicht! Du bist eine Legende! 🎯
          </div>
        </motion.div>
      )}



      {Object.values(gardenAreas || {}).some(area => area.level === 1000) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-xl p-6 shadow-lg"
        >
          <h3 className="font-bold text-yellow-800 mb-3 text-xl">
            👑 PERFEKTION ERREICHT! 👑
          </h3>
          <p className="text-yellow-700 text-lg">
            LEGENDÄR! Du hast einen Bereich auf das Maximum Level 1000 gebracht! 
            Du bist jetzt offiziell ein Garten-Gott!
          </p>
        </motion.div>
      )}



      {/* Fortgeschrittene Tipps */}
      {completedAreas > 0 && completedAreas < 4 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6 shadow-md"
        >
          <h3 className="font-bold text-green-800 mb-3 text-base sm:text-lg">
            🚀 Du machst Fortschritte!
          </h3>
          <p className="text-green-700 mb-3 text-sm sm:text-base">
            Super! Du hast bereits {completedAreas} Bereich{completedAreas > 1 ? 'e' : ''} fertiggestellt. 
            Hier sind einige Tipps für die nächsten Schritte:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="font-semibold text-gray-800 mb-1">🏠 Gewächshaus</div>
              <div className="text-gray-600">Wetterunabhängiges Wachstum für konstante Erträge</div>
            </div>
            <div className="bg-white rounded-lg p-3 shadow-sm">
              <div className="font-semibold text-gray-800 mb-1">⛲ Springbrunnen</div>
              <div className="text-gray-600">Verstärkt alle anderen Bereiche - eine lohnende Investition!</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Experten-Hinweis */}
      {completedAreas >= 6 && completedAreas < 10 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-xl p-6 shadow-md"
        >
          <h3 className="font-bold text-red-800 mb-3 text-lg">
            🔥 Experten-Herausforderung
          </h3>
          <p className="text-red-700 mb-3">
            Du bist bereit für die ultimative Herausforderung! Die Experten-Bereiche 
            <strong> Obstgarten</strong> und <strong>Gemüsebeet</strong> warten auf dich.
          </p>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-center">
              <div className="text-2xl mb-2">🍎🥕</div>
              <div className="font-semibold text-gray-800">Experten-Bereiche</div>
              <div className="text-sm text-gray-600">
                Höchste Belohnungen, aber auch höchste Kosten!
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Legendäre Bereiche-Hinweis */}
      {completedAreas >= 10 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 border-2 border-purple-300 rounded-xl p-4 sm:p-6 shadow-lg"
        >
          <h3 className="font-bold text-purple-800 mb-3 text-lg sm:text-xl flex items-center gap-2">
            ✨ Legendäre Garten-Magie ✨
          </h3>
          <p className="text-purple-700 mb-4 text-sm sm:text-base">
            Unglaublich! Du hast {completedAreas} Bereiche gemeistert. Jetzt erwarten dich die 
            <strong> legendären Bereiche</strong> - mystische Orte voller Magie und unermesslicher Macht!
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-sm">
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm border border-purple-200">
              <div className="text-center">
                <div className="text-xl sm:text-2xl mb-1">🧚‍♀️</div>
                <div className="font-semibold text-gray-800 text-xs sm:text-sm">Verzauberter Hain</div>
                <div className="text-xs text-gray-600">Feenmagie x3</div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm border border-purple-200">
              <div className="text-center">
                <div className="text-xl sm:text-2xl mb-1">💎</div>
                <div className="font-semibold text-gray-800 text-xs sm:text-sm">Kristallhöhle</div>
                <div className="text-xs text-gray-600">Passive Gems</div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm border border-purple-200">
              <div className="text-center">
                <div className="text-xl sm:text-2xl mb-1">🌙</div>
                <div className="font-semibold text-gray-800 text-xs sm:text-sm">Mondschein-Garten</div>
                <div className="text-xs text-gray-600">Mondphasen</div>
              </div>
            </div>
            <div className="bg-white rounded-lg p-2 sm:p-3 shadow-sm border border-purple-200">
              <div className="text-center">
                <div className="text-xl sm:text-2xl mb-1">🐉</div>
                <div className="font-semibold text-gray-800 text-xs sm:text-sm">Drachenhort</div>
                <div className="text-xs text-gray-600">x10 Multi!</div>
              </div>
            </div>
          </div>
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium">
              <span>⚠️</span>
              <span className="hidden sm:inline">Warnung: Erfordert Level 15+ und enormen Reichtum!</span>
              <span className="sm:hidden">Level 15+ & viel Geld!</span>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}; 